// src/pages/dynamic/DynamicModuleLoader.jsx
import { Suspense, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import modulesMap from './modulesMap';

/**
 * Convert a key like "tyre-item/:id" into a case-insensitive regex
 * that matches an actual path like "tyre-item/123".
 */
function keyToRegex(key) {
  // ensure leading/trailing slashes ignored
  const cleaned = key.replace(/^\/+|\/+$/g, '');
  // escape regex special chars except parameter tokens
  const esc = cleaned.replace(/([.+?^=!:${}()|[\]\\/])/g, '\\$1');
  const pattern = '^' + esc.replace(/\\:([^/\\]+)/g, '([^/]+)') + '$';
  return new RegExp(pattern, 'i');
}

/**
 * Extract named params from a key pattern like "foo/:id/bar/:sub"
 * using the actual fullPath segments.
 */
function extractParamsFromKey(fullPath, key) {
  const keyParts = key.split('/').filter(Boolean);
  const pathParts = fullPath.split('/').filter(Boolean);
  const params = {};
  for (let i = 0; i < keyParts.length; i++) {
    const kp = keyParts[i];
    if (kp && kp.startsWith(':')) {
      const name = kp.slice(1);
      params[name] = pathParts[i] ?? undefined;
    }
  }
  return params;
}

const IS_DEV_MODE = import.meta.env.VITE_APP_DEPLOY_MODE !== 'prod';

const DEV_WHITELISTED_KEYS = ['driver-performance'];

const checkAccess = (fullPath, matchedKey) => {
  try {
    const rawMenu = localStorage.getItem('master_menu');
    // Jika tidak ada master_menu (belum login / error system), kita skip check atau block?
    // Untuk amannya, asumsikan jika tidak ada = forbid, kecuali ini initial load
    if (!rawMenu) return false;

    const modules = JSON.parse(rawMenu);

    const hasAccess = (nodes, targetPath, targetKey) => {
      for (const node of nodes) {
        // match by modules_path, modules_name, or alias
        const nodePath = (node.modules_path || '').replace(/^\/+|\/+$/g, '').toLowerCase();
        const nodeKey = (node.modules_name || '').toLowerCase();

        if (nodePath === targetPath || nodePath === targetKey || nodeKey === targetKey) {
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (hasAccess(node.children, targetPath, targetKey)) return true;
        }
      }
      return false;
    };

    let isAuthorized = hasAccess(modules, fullPath.toLowerCase(), matchedKey.toLowerCase());

    if (!isAuthorized) {
      const mk = matchedKey.toLowerCase();
      if (mk === 'history-download-video' || mk === 'history-video') {
        isAuthorized = hasAccess(modules, 'live-streaming', 'live-streaming');
      }
      if (mk === 'settings-driver') {
        isAuthorized = hasAccess(modules, 'master-driver', 'master-driver');
      }
      if (mk === 'setting-score' || mk === 'driver-score-setting' || mk === 'driver-score-periodic') {
        isAuthorized =
          hasAccess(modules, 'driver-management', 'driver-management') ||
          hasAccess(modules, 'driver-performance', 'driver-performance') ||
          hasAccess(modules, 'master-driver', 'master-driver');
      }
      if (
        mk === 'data-reports' ||
        mk === 'report-sensor' ||
        mk === 'report-waypoint' ||
        mk === 'data-reports?name=vehicle_report' ||
        mk === 'data-reports?name=driver_report' ||
        mk === 'data-reports?name=trip_report' ||
        mk === 'data-reports?name=ritase_summary' ||
        mk === 'data-reports?name=ritase_details' ||
        mk === 'battery-management' ||
        mk === 'charging-management'
      ) {
        isAuthorized = hasAccess(modules, 'report-catalog', 'report-catalog');
      }
    }

    return isAuthorized;
  } catch {
    return false;
  }
};

const WHITELISTED_KEYS = [
  'profile',
  'dashboard',
  'maps-monitoring',
  'dashboard-vehicle',
  'dashboard-asset',
  'dashboard-expense',
  'dashboard-revenue',
  '/shared/trip',
  '/shared/live'
];

export default function DynamicModuleLoader() {
  const location = useLocation();

  // normalize fullPath without leading/trailing slashes
  const fullPath = location.pathname.replace(/^\/+|\/+$/g, '');
  const qs = new URLSearchParams(location.search);
  const nameFromQuery = (qs.get('name') || '').trim();

  const matchedKey = useMemo(() => {
    const keys = Object.keys(modulesMap || {});
    if (!fullPath && !nameFromQuery) return null;

    // 1) exact match fullPath
    const exact = keys.find((k) => k.replace(/^\/+|\/+$/g, '').toLowerCase() === fullPath.toLowerCase());
    if (exact) return exact;

    // 2) pattern match (keys may contain :params)
    const patternMatch = keys.find((k) => {
      try {
        return keyToRegex(k).test(fullPath);
      } catch (e) {
        console.warn('keyToRegex error for key=', k, e);
        return false;
      }
    });
    if (patternMatch) return patternMatch;

    // 3) if query name provided, try matching by name heuristics
    if (nameFromQuery) {
      const name = nameFromQuery.toLowerCase();

      // a) key exactly equals name (key may be like 'tyre-item')
      const byName = keys.find((k) => k.toLowerCase() === name);
      if (byName) return byName;

      // b) last segment equals name (key may be 'master/tyre-item' or 'tyre-item/:id')
      const byLastSegment = keys.find((k) => {
        const parts = k.split('/').filter(Boolean);
        return parts[parts.length - 1].toLowerCase().replace(/^:|[:]/g, '') === name;
      });
      if (byLastSegment) return byLastSegment;

      // c) endsWith name
      const byEndsWith = keys.find((k) => k.toLowerCase().endsWith('/' + name) || k.toLowerCase().endsWith(name));
      if (byEndsWith) return byEndsWith;
    }

    // 4) fuzzy: remove dashes/underscores from both and compare
    const simplified = fullPath.replace(/[-_]/g, '').toLowerCase();
    const fuzzy = keys.find((k) => k.replace(/[-_]/g, '').toLowerCase() === simplified);
    if (fuzzy) return fuzzy;

    return null;
  }, [fullPath, nameFromQuery]);

  if (!matchedKey) {
    // redirect to 404 (you can change to inline message)
    return <Navigate to="/maintenance/404" replace />;
  }

  // Cek otorisasi path dari master_menu di local storage
  const isDevBypassed = IS_DEV_MODE && DEV_WHITELISTED_KEYS.includes(matchedKey.toLowerCase());
  if (!isDevBypassed && !WHITELISTED_KEYS.includes(matchedKey.toLowerCase())) {
    const rawMenu = typeof window !== 'undefined' ? localStorage.getItem('master_menu') : null;
    if (!rawMenu) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40, marginTop: '20vh' }}>
          <CircularProgress />
        </div>
      );
    }

    const isAuthorized = checkAccess(fullPath, matchedKey);
    if (!isAuthorized) {
      return <Navigate to="/maintenance/403" replace />;
    }
  }

  const ModuleComp = modulesMap[matchedKey];
  if (!ModuleComp) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Module not available</h2>
        <p>
          Module <strong>{matchedKey}</strong> is registered but component is missing.
        </p>
      </div>
    );
  }

  // extract params from pattern key (if any)
  const params = extractParamsFromKey(fullPath, matchedKey);

  return (
    <Suspense
      fallback={
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress />
        </div>
      }
    >
      {/* pass moduleName (from query or last segment) and extracted params */}
      <ModuleComp moduleName={nameFromQuery || matchedKey} routeParams={params} />
    </Suspense>
  );
}
